import React from 'react';
import { PauseCircle, ChevronRight } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const WatchesView = ({ 
  savedWatches, 
  loadWatch, 
  deleteWatch, 
  isTrialPaused = false, 
  onOpenPaywall,
  userProfile 
}) => {
  const { language } = useLanguage();
  const isEn = language === 'English' || language === 'en';

  const rawStatus = userProfile?.status || userProfile?.subscription_tier;
  const currentTier = (rawStatus === 'basic' || rawStatus === 'premium') ? rawStatus : 'trial';

  const tierLimits = {
    trial: 1,
    basic: 5,
    premium: 20
  };
  const maxWatches = tierLimits[currentTier] || 1;
  const currentCount = savedWatches ? savedWatches.length : 0;

  const pausedTitle = isEn 
    ? 'Your watches are paused (20/20 summaries)' 
    : 'Dina bevakningar är pausade (20/20 sammanfattningar)';

  const pausedDesc = isEn
    ? 'You have reached the trial limit. Upgrade to Basic to resume nightly monitoring.'
    : 'Du har nått gränsen för provperioden. Uppgradera till Basic för att återuppta nattliga bevakningar.';

  const pausedBtnText = isEn 
    ? 'Reactivate with Basic (25 SEK/mo)' 
    : 'Återaktivera med Basic (25 kr/mån)';

  return (
    <div className="max-w-3xl mx-auto pb-32 md:pb-10 px-4 md:px-0 mt-8 md:mt-12">
      
      {/* --- PAUS-BANNER VID NÅDD PROVKVOT --- */}
      {isTrialPaused && (
        <div className="mb-6 p-4 md:p-5 bg-amber-50/90 border border-amber-200/80 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm animate-fade-in">
          <div className="flex items-start gap-3">
            <PauseCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-amber-950">
                {pausedTitle}
              </h4>
              <p className="text-xs text-amber-800/90 mt-0.5 leading-relaxed">
                {pausedDesc}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenPaywall}
            className="w-full sm:w-auto shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 active:scale-95 cursor-pointer"
          >
            <span>{pausedBtnText}</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* --- HEADER MED KVOTINDIKATOR --- */}
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 text-center md:text-left">
        <div>
          <h1 className="text-3xl font-serif font-bold text-slate-900 mb-2">{t(language, 'watches_title')}</h1>
          <p className="text-slate-500 text-sm md:text-lg">{t(language, 'watches_subtitle')}</p>
        </div>
        <div className="inline-flex items-center justify-center self-center md:self-auto px-3.5 py-1.5 bg-slate-100 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">
          <span>{currentCount} / {maxWatches} {isEn ? 'watches used' : 'bevakningar använda'}</span>
        </div>
      </div>

      {(!savedWatches || savedWatches.length === 0) ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center mt-6">
          <span className="text-6xl mb-4 block">🤖</span>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{t(language, 'watches_empty_title')}</h2>
          <p className="text-slate-500 mb-6">{t(language, 'watches_empty_desc')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {savedWatches.map(watch => (
            <div 
              key={watch.id} 
              className={`bg-white border rounded-2xl p-5 md:p-6 relative group hover:shadow-md transition-all duration-300 ${
                isTrialPaused ? 'border-amber-200/80 bg-amber-50/10' : 'border-slate-200'
              }`}
            >
              
              <button
                onClick={() => deleteWatch(watch.id)}
                className="absolute top-4 right-4 text-slate-300 hover:text-red-500 w-11 h-11 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors md:opacity-0 md:group-hover:opacity-100 cursor-pointer"
                title={t(language, 'watches_delete')}
              >
                <span className="text-xl">🗑️</span>
              </button>

              {/* STATUSBRICKA */}
              <div className="flex items-center mb-4">
                {isTrialPaused ? (
                  <div className="flex items-center">
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                    </span>
                    <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">
                      {isEn ? 'Paused (limit reached)' : 'Pausad (provtak nått)'}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                    <span className="text-xs font-bold text-green-600 uppercase tracking-wider">{t(language, 'watches_status_active')}</span>
                  </div>
                )}
              </div>

              <h3 className="text-xl font-bold text-slate-800 mb-4 pr-12">{watch.title}</h3>

              <div className="flex flex-wrap gap-2 mb-6">
                {watch.author_data && (
                  <span className="bg-slate-50 text-slate-600 text-xs px-3 py-1.5 rounded-lg border border-slate-200 flex items-center">
                    <span className="mr-1">✍️</span> {watch.author_data.name}
                  </span>
                )}
                {watch.journal_data && (
                  <span className="bg-slate-50 text-slate-600 text-xs px-3 py-1.5 rounded-lg border border-slate-200 flex items-center">
                    <span className="mr-1">📖</span> {watch.journal_data.name}
                  </span>
                )}
                {watch.custom_query && (
                  <span className="bg-slate-50 text-slate-600 text-xs px-3 py-1.5 rounded-lg border border-slate-200 flex items-center">
                    <span className="mr-1">🔍</span> {watch.custom_query}
                  </span>
                )}
                {watch.topics && watch.topics.length > 0 && watch.topics.map(tData => (
                  <span key={tData.name} className="bg-blue-50 text-blue-700 text-xs px-3 py-1.5 rounded-lg border border-blue-100 flex items-center">
                    <span className="mr-1">{tData.icon || '📌'}</span> {t(language, tData.labelKey || tData.name)}
                  </span>
                ))}
              </div>

              <button
                onClick={() => loadWatch(watch)}
                className="w-full md:w-auto bg-slate-50 text-slate-700 font-bold py-3 px-6 rounded-xl hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 border border-slate-200 transition-colors min-h-[44px] flex items-center justify-center shadow-sm active:scale-95 cursor-pointer"
              >
                <span className="mr-2">⚙️</span> {t(language, 'watches_btn_edit')}
              </button>
              
            </div>
          ))}
        </div>
      )}
      
    </div>
  );
};

export default WatchesView;